import React from 'react';
import type { CaptureProps, ImportAdapter, ImportedItem } from './types';
import { parseBrowserCsv } from './browserCsv';
import { csvHeaderSet } from './detect';
import { readCsvFileTexts } from './captureFiles';

/**
 * Parse a Firefox Password Manager CSV export.
 *
 * Firefox's about:logins export columns are
 * `url,username,password,httpRealm,formActionOrigin,guid,timeCreated,
 * timeLastUsed,timePasswordChanged`. Only the first three are credentials; the
 * rest is Firefox's own bookkeeping and is dropped.
 *
 * Because there is **no `name` column**, every item is named by its URL —
 * `parseBrowserCsv`'s existing fallback, not a Firefox special case. Users
 * rename in the wizard's review step.
 *
 * Firefox doesn't export notes, TOTP, folder, or favorite.
 *
 * Shares `parseBrowserCsv` with Google: the two exports are the same shape over
 * the columns we read. This stays a separate adapter so items carry
 * `importSource: 'firefox'` — that's what lets the autofill suggestion list
 * distinguish a Firefox-imported duplicate from a Chrome-imported one.
 */
export function parseFirefox(csv: string): ImportedItem[] {
  return parseBrowserCsv(csv, 'firefox');
}

// ─── CaptureComponent ─────────────────────────────────────────────────────────

const FIREFOX_EXPORT_STEPS = [
  'Open Firefox and visit about:logins.',
  'Click the ⋯ menu at the top right of the list, then choose "Export Passwords…".',
  'Confirm the warning, then re-authenticate with your device password when Firefox asks.',
  'Firefox saves a file called "passwords.csv" to your downloads.',
];

function FirefoxCapture({ onCapture, onError }: CaptureProps) {
  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const texts = await readCsvFileTexts(e.target.files, onError, 'Firefox Password Manager CSV export');
    if (texts) onCapture(texts);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-surface border border-divider rounded-xl p-4">
        <h3 className="text-sm font-semibold text-ink mb-2">How to export from Firefox Password Manager</h3>
        <ol className="text-sm text-ink-secondary list-decimal list-inside space-y-1">
          {FIREFOX_EXPORT_STEPS.map((step, i) => <li key={i}>{step}</li>)}
        </ol>
      </div>

      <div className="flex flex-col items-center gap-4 p-6 border-2 border-dashed border-divider rounded-xl">
        <svg className="w-10 h-10 text-ink-faint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <label className="cursor-pointer px-4 py-2 bg-hbs-primary hover:bg-hbs-primary-hover text-hbs-on-primary text-sm font-medium rounded-lg transition-colors">
          Choose CSV file(s)
          <input type="file" accept=".csv" multiple className="hidden" onChange={handleFiles} />
        </label>
      </div>
    </div>
  );
}

/** Firefox CSV carries bookkeeping columns no other source we read has —
 *  `formActionOrigin`, `httpRealm`, `guid`. Any one (with `password`) is a
 *  confident Firefox signal, and it must be checked before Google since both
 *  share `url,username,password`. */
function detectFirefox(rawInput: string): boolean {
  const h = csvHeaderSet(rawInput);
  return h.has('password')
    && (h.has('formactionorigin') || h.has('httprealm') || h.has('guid'));
}

export const FirefoxAdapter: ImportAdapter = {
  id: 'firefox',
  name: 'Firefox Password Manager',
  description: 'Import from Firefox Password Manager (the passwords Firefox saves for you).',
  CaptureComponent: FirefoxCapture,
  parse: parseFirefox,
  detect: detectFirefox,
};
